/**
 * Vorschau eines KIAN-Entwurfs: das Ticket, wie es jetzt ist, und was der Klick daran ändert —
 * Felder als „jetzt → danach", neue Kommentare im Wortlaut über dem bisherigen Verlauf.
 * Bis dahin stand in der Karte nur „1 Kommentar"; was genau ins Ticket geht, war nicht zu sehen.
 *
 * Den aktuellen Stand liefert derselbe Abruf wie das Cockpit (`['weclapp-cockpit', id]`, Spiegel
 * zuerst). Ohne ihn vergleicht die Vorschau mit dem Ticketkopf aus dem Entwurf und sagt das.
 * Geöffnet wird das Ticket in einem neuen Tab: Das Cockpit-Drawer läge unter dem KIAN-Flyout.
 */
import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  IconArrowRight,
  IconChevronDown,
  IconChevronUp,
  IconExternalLink,
  IconLock,
  IconMessage,
  IconPencil,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { api } from '../../../lib/api';
import type { WeclappTicketComment } from '../../../lib/api/types/weclapp';
import type { KianAction, KianActionItem } from '../../../lib/api/domains/kianActions';
import EmailDescription from '../../EmailDescription';
import { isItemEditable, ticketChangeRows, ticketHref, type KianTicketCurrent } from './kianActionUi';

const VISIBLE_HISTORY = 5;

interface Props {
  opened: boolean;
  onClose: () => void;
  action: KianAction;
  item: KianActionItem | null;
  /** Entwurf bearbeiten (Editor der Karte) — fehlt bei archivierten Gesprächen */
  onEdit?: (item: KianActionItem) => void;
  /** Nur diesen Eintrag ausführen — fehlt, wenn er (noch) nicht ausführbar ist */
  onExecute?: (item: KianActionItem) => void;
  executing?: boolean;
}

/** weclapp liefert Zeitstempel mal als Unix-ms (auch als String), mal als ISO. */
function toDate(v?: string | number | null): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? Number(v) : v;
  const d = Number.isFinite(n) ? new Date(n) : new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function isoDay(v?: string | number | null): string | null {
  const d = toDate(v);
  if (!d) return null;
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

function formatStamp(v?: string | number | null): string {
  const d = toDate(v);
  return d
    ? d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '–';
}

/** Entfernt das „(username)"-Suffix aus aufgelösten weclapp-Namen (wie im Cockpit). */
function displayName(name?: string | null): string | null {
  if (!name) return null;
  return name.replace(/\s*\([^)]*\)\s*$/, '').trim() || name;
}

function looksLikeHtml(s: string): boolean {
  return /<[a-z!/][^>]*>/i.test(s) || /&(?:nbsp|amp|lt|gt|quot|#\d+);/i.test(s);
}

function CommentBox({
  internal,
  author,
  when,
  fresh,
  children,
}: {
  internal: boolean;
  author?: string | null;
  when?: string | null;
  fresh?: boolean;
  children: ReactNode;
}) {
  const color = internal ? 'orange' : 'blue';
  return (
    <Box
      style={{
        background: 'var(--mantine-color-dark-6)',
        borderRadius: 6,
        padding: '0.5rem 0.75rem',
        borderLeft: `3px solid var(--mantine-color-${color}-6)`,
        outline: fresh ? '1px dashed var(--mantine-color-cyberOrange-5, var(--mantine-color-orange-5))' : undefined,
        outlineOffset: fresh ? 2 : undefined,
      }}
    >
      <Group gap="xs" mb={4} wrap="wrap">
        {internal ? <IconLock size={14} color="var(--mantine-color-orange-4)" /> : <IconMessage size={14} color="var(--mantine-color-blue-4)" />}
        {author ? (
          <Text size="sm" fw={600} c="gray.2">
            {author}
          </Text>
        ) : null}
        {when ? (
          <Text size="xs" c="dimmed">
            {when}
          </Text>
        ) : null}
        {internal ? (
          <Badge size="xs" color="orange" variant="light">
            Intern
          </Badge>
        ) : (
          <Badge size="xs" color="blue" variant="light">
            Öffentlich — Kunde sieht ihn
          </Badge>
        )}
        {fresh ? (
          <Badge size="xs" color="cyberOrange" variant="filled">
            Neu — noch nicht geschrieben
          </Badge>
        ) : null}
      </Group>
      {children}
    </Box>
  );
}

export function KianTicketChangePreview({ opened, onClose, action, item, onEdit, onExecute, executing }: Props) {
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showOldDescription, setShowOldDescription] = useState(false);

  const create = action.kind === 'TICKET_CREATE';
  const resolved = item?.resolved ?? null;
  const ticketId = create ? item?.ticketId ?? null : (resolved?.ticket?.id ?? item?.ticketId ?? null);

  const cockpit = useQuery({
    queryKey: ['weclapp-cockpit', ticketId],
    queryFn: () => api.getWeclappTicketCockpit(ticketId!),
    enabled: opened && !!ticketId && !create,
    staleTime: 30_000,
  });

  const ticket = cockpit.data?.ticket ?? null;
  const current = useMemo<KianTicketCurrent | null>(() => {
    if (!ticket) return null;
    return {
      subject: ticket.subject,
      partyId: ticket.partyId,
      partyName: ticket.partyName,
      statusId: ticket.statusId,
      status: ticket.status,
      priorityId: ticket.priorityId,
      priority: ticket.priority,
      categoryId: ticket.ticketCategoryId,
      category: ticket.ticketCategoryName,
      assigneeId: ticket.responsibleUserId,
      assignee: displayName(ticket.responsibleUserName),
      dueDate: isoDay(ticket.dueDate),
    };
  }, [ticket]);

  const rows = useMemo(() => (item ? ticketChangeRows(action.kind, item, current) : []), [action.kind, item, current]);

  const newComments = useMemo(() => {
    if (!item) return [];
    if (resolved?.comments?.length) return resolved.comments;
    return (item.draft?.comments ?? [])
      .filter((c) => c.text.trim())
      .map((c) => ({ text: c.text, internal: c.internal ?? action.options.commentsInternal }));
  }, [item, resolved, action.options.commentsInternal]);

  const history = useMemo(() => {
    const list = [...(cockpit.data?.comments ?? [])];
    list.sort((a, b) => (toDate(b.createdDate)?.getTime() ?? 0) - (toDate(a.createdDate)?.getTime() ?? 0));
    return list;
  }, [cockpit.data]);
  const visibleHistory = showAllHistory ? history : history.slice(0, VISIBLE_HISTORY);

  if (!item) return null;

  const number = resolved?.ticket?.number ?? item.ticketNumber ?? null;
  const subject = ticket?.subject ?? resolved?.ticket?.subject ?? resolved?.subject ?? item.draft?.subject ?? null;
  const party = ticket?.partyName ?? resolved?.ticket?.partyName ?? resolved?.customer?.name ?? null;
  const statusNow = ticket?.status ?? resolved?.ticket?.statusName ?? null;
  const href = ticketHref({ ticketNumber: number, ticketId });
  const newDescription = resolved?.description ?? null;
  const oldDescription = ticket?.description ?? '';
  const textChange = !create && (rows.some((r) => r.field === 'subject') || !!newDescription);
  const publicComments = newComments.filter((c) => !c.internal).length;
  const issues = item.issues;
  const editable = isItemEditable(item, action.kind) && !!onEdit;
  const loadingCurrent = !create && !!ticketId && cockpit.isLoading;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={create ? 'Vorschau — so wird das Ticket angelegt' : `Vorschau — ${number ?? 'Ticket'} nach der Änderung`}
      size="xl"
      centered={!isMobile}
      fullScreen={isMobile}
      zIndex={5300}
    >
      <Stack gap="md">
        {/* Kopf: welches Ticket, wie es jetzt dasteht */}
        <Paper withBorder radius="md" p="sm">
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
            <Stack gap={2} style={{ minWidth: 0, flex: '1 1 16rem' }}>
              <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>
                {number ? `${number} · ` : create ? 'Neues Ticket · ' : ''}
                {subject ?? '(ohne Betreff)'}
              </Text>
              <Group gap={6} wrap="wrap">
                {party ? (
                  <Text size="xs" c="dimmed">
                    {party}
                  </Text>
                ) : null}
                {!create && statusNow ? (
                  <Badge size="xs" variant="light" color="gray">
                    jetzt: {statusNow}
                  </Badge>
                ) : null}
                {!create && (current?.assignee ?? resolved?.ticket?.assigneeName) ? (
                  <Text size="xs" c="dimmed">
                    Bearbeiter: {current?.assignee ?? resolved?.ticket?.assigneeName}
                  </Text>
                ) : null}
              </Group>
            </Stack>
            {href ? (
              <Button
                component="a"
                href={href}
                target="_blank"
                rel="noopener"
                size="xs"
                variant="light"
                leftSection={<IconExternalLink size={14} />}
              >
                Ticket öffnen
              </Button>
            ) : null}
          </Group>
          {!create && cockpit.isError ? (
            <Text size="xs" c="yellow" mt={6}>
              Der aktuelle Stand des Tickets ließ sich nicht laden — verglichen wird mit dem Stand beim Anlegen des Entwurfs.
            </Text>
          ) : null}
        </Paper>

        {issues.length ? (
          <Stack gap={4}>
            {issues.map((issue, idx) => (
              <Text key={idx} size="xs" c={issue.level === 'error' ? 'red' : issue.level === 'warning' ? 'yellow' : 'dimmed'}>
                {issue.message}
              </Text>
            ))}
          </Stack>
        ) : null}

        {/* Felder */}
        <Stack gap={6}>
          <Text size="sm" fw={600}>
            {create ? 'Angaben' : 'Was sich am Ticket ändert'}
          </Text>
          {loadingCurrent ? (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">
                Aktueller Stand wird geladen …
              </Text>
            </Group>
          ) : rows.length ? (
            <Table withTableBorder verticalSpacing={6} horizontalSpacing="sm" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={isMobile ? 90 : 130}>Feld</Table.Th>
                  {!create ? <Table.Th>Jetzt</Table.Th> : null}
                  {!create ? <Table.Th w={24} /> : null}
                  <Table.Th>{create ? 'Wert' : 'Danach'}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.field}>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {row.label}
                      </Text>
                    </Table.Td>
                    {!create ? (
                      <Table.Td>
                        <Text size="sm" c="dimmed" td={row.beforeIsValue ? 'line-through' : undefined} style={{ wordBreak: 'break-word' }}>
                          {row.before}
                        </Text>
                      </Table.Td>
                    ) : null}
                    {!create ? (
                      <Table.Td px={0}>
                        <IconArrowRight size={14} color="var(--mantine-color-dimmed)" />
                      </Table.Td>
                    ) : null}
                    <Table.Td>
                      <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>
                        {row.after}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text size="sm" c="dimmed">
              {create
                ? 'Keine weiteren Angaben — es gelten die Standardwerte.'
                : newComments.length || newDescription
                  ? 'Betreff, Status, Priorität, Kategorie, Bearbeiter und Frist bleiben, wie sie sind.'
                  : 'Keine Änderung an den Feldern.'}
            </Text>
          )}
          {textChange ? (
            <Text size="xs" c="dimmed">
              Der bisherige Betreff und die bisherige Beschreibung werden als interne Notiz am Ticket gesichert.
            </Text>
          ) : null}
        </Stack>

        {/* Beschreibung */}
        {newDescription || (create && item.draft?.description) ? (
          <Stack gap={6}>
            <Text size="sm" fw={600}>
              {create ? 'Beschreibung' : 'Neue Beschreibung (ersetzt die bisherige)'}
            </Text>
            <Paper withBorder radius="md" p="sm" style={{ borderColor: 'var(--mantine-color-cyberOrange-7, var(--mantine-color-orange-7))' }}>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {newDescription ?? item.draft?.description}
              </Text>
            </Paper>
            {!create && oldDescription ? (
              <Box>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  color="gray"
                  rightSection={showOldDescription ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                  onClick={() => setShowOldDescription((v) => !v)}
                >
                  Bisherige Beschreibung
                </Button>
                <Collapse expanded={showOldDescription}>
                  <Box mt={6} style={{ maxHeight: 320, overflow: 'auto' }}>
                    <EmailDescription html={oldDescription} ticketLayout />
                  </Box>
                </Collapse>
              </Box>
            ) : null}
          </Stack>
        ) : null}

        {/* Verlauf: neue Kommentare oben, darunter, was schon im Ticket steht */}
        <Stack gap={6}>
          <Group justify="space-between" gap="xs">
            <Text size="sm" fw={600}>
              {create ? `Kommentare (${newComments.length})` : 'Verlauf nach der Änderung'}
            </Text>
            {!create && history.length ? (
              <Text size="xs" c="dimmed">
                {newComments.length} neu · {history.length} schon im Ticket
              </Text>
            ) : null}
          </Group>
          {publicComments > 0 ? (
            <Alert color="yellow" variant="light" py={6}>
              {publicComments === 1 ? 'Ein Kommentar ist öffentlich' : `${publicComments} Kommentare sind öffentlich`} — der Kunde
              sieht ihn im Kundenportal, ggf. auch per Mail von weclapp.
            </Alert>
          ) : null}
          {newComments.map((c, idx) => (
            // Nach einem abgebrochenen Lauf stehen die ersten schon im Ticket — der Lauf setzt dahinter fort
            <CommentBox key={`new-${idx}`} internal={c.internal} author="du" fresh={idx >= item.commentsDone}>
              <Text size="sm" lh={1.55} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {c.text}
              </Text>
            </CommentBox>
          ))}
          {!newComments.length ? (
            <Text size="xs" c="dimmed">
              Kein neuer Kommentar.
            </Text>
          ) : null}

          {!create && cockpit.data ? (
            history.length ? (
              <>
                {visibleHistory.map((c: WeclappTicketComment) => (
                  <CommentBox
                    key={c.id}
                    internal={!!c.internal}
                    author={displayName(c.createdByUserName)}
                    when={formatStamp(c.createdDate)}
                  >
                    {c.htmlComment ? (
                      <EmailDescription html={c.htmlComment} />
                    ) : c.comment && looksLikeHtml(c.comment) ? (
                      <EmailDescription html={c.comment} />
                    ) : (
                      <Text size="sm" c="gray.1" lh={1.55} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {c.comment}
                      </Text>
                    )}
                  </CommentBox>
                ))}
                {history.length > VISIBLE_HISTORY ? (
                  <Button variant="subtle" size="compact-xs" onClick={() => setShowAllHistory((v) => !v)}>
                    {showAllHistory ? 'Weniger anzeigen' : `Alle ${history.length} bisherigen Kommentare anzeigen`}
                  </Button>
                ) : null}
              </>
            ) : (
              <Text size="xs" c="dimmed">
                Bisher keine Kommentare im Ticket.
              </Text>
            )
          ) : null}
          {!create && cockpit.data?.commentsStale ? (
            <Text size="xs" c="yellow">
              Der Verlauf konnte gerade nicht bei weclapp aufgefrischt werden — angezeigt wird der zuletzt bekannte Stand.
            </Text>
          ) : null}
        </Stack>

        <Group justify="space-between" mt="xs" wrap="wrap" gap="xs">
          <Text size="xs" c="dimmed">
            Noch nichts geschrieben — erst der Klick auf „{create ? 'Ticket anlegen' : 'Änderung ausführen'}" schreibt.
          </Text>
          <Group gap="xs">
            <Button variant="default" size="xs" onClick={onClose}>
              Schließen
            </Button>
            {editable && onEdit ? (
              <Button variant="light" size="xs" leftSection={<IconPencil size={14} />} onClick={() => onEdit(item)}>
                Bearbeiten
              </Button>
            ) : null}
            {onExecute ? (
              <Button
                size="xs"
                color="cyberOrange"
                leftSection={<IconPlayerPlay size={14} />}
                loading={executing}
                onClick={() => onExecute(item)}
              >
                {create ? 'Ticket anlegen' : 'Änderung ausführen'}
              </Button>
            ) : null}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
